import { useState, useCallback } from 'react';

type PropGetterOptions = {
  [key: string]: unknown;
};

type CloseButtonProps = {
  'aria-label': 'close';
  onClick: () => void;
};

type ModalProps = {
  onClose: () => void;
  isOpen: boolean;
};

type ModalOptions = {
  initialOpen?: boolean;
};

type Modal = {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  getModalProps: <T extends PropGetterOptions>(options?: PropGetterOptions) => T & ModalProps;
  getCloseButtonProps: <T extends PropGetterOptions>(options?: PropGetterOptions) => T & CloseButtonProps;
};

function useModal({ initialOpen = false }: ModalOptions = {}): Modal {
  const [isOpen, setIsOpen] = useState(initialOpen);

  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);

  function getModalProps<T extends PropGetterOptions>({ onClose, ...props }: PropGetterOptions = {}): T & ModalProps {
    return {
      onClose: () => {
        if (typeof onClose === 'function') {
          onClose();
        }
        closeModal();
      },
      isOpen,
      ...(props as T),
    };
  }

  function getCloseButtonProps<T extends PropGetterOptions>({ onClick, ...props }: PropGetterOptions = {}): T &
    CloseButtonProps {
    return {
      'aria-label': 'close',
      onClick: () => {
        if (typeof onClick === 'function') {
          onClick();
        }
        closeModal();
      },
      ...(props as T),
    };
  }

  return {
    isOpen,
    openModal,
    closeModal,
    getModalProps,
    getCloseButtonProps,
  };
}

export default useModal;
